import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from './ui/Icons';
import { AppHeader } from './ui/AppHeader';
import { UserProfile, Organization, SubscriptionTier } from '../types';
import { getAIQuotaReport } from '../services/aiQuotaService';
import { getUserProfile } from '../services/firebase';

interface AIUsageAdminProps {
  user: UserProfile;
  organization: Organization | null;
  onLogout: () => void;
}

export const AIUsageAdmin: React.FC<AIUsageAdminProps> = ({
  user,
  organization,
  onLogout
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('admin');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<{
    tier: SubscriptionTier;
    period: string;
    isLifetime: boolean;
    images: { used: number; limit: number; remaining: number };
    tts: { used: number; limit: number; remaining: number };
    videos: { used: number; limit: number; remaining: number };
    recentUsage: Array<{
      timestamp: number;
      userId: string;
      feature: string;
      details?: string;
      imageUrl?: string;
      audioUrl?: string;
      videoUrl?: string;
    }>;
  } | null>(null);
  const [userNames, setUserNames] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadUsageReport = async () => {
      if (!organization) {
        setError('No organization found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await getAIQuotaReport(organization.id);
        setReport(data);
        setError(null);

        // Fetch user names for all users in the recent usage
        const uniqueUserIds = [...new Set(data.recentUsage.map(u => u.userId))];
        const names: Record<string, string> = {};

        await Promise.all(
          uniqueUserIds.map(async (userId) => {
            try {
              const userProfile = await getUserProfile(userId);
              names[userId] = userProfile?.displayName || 'Unknown User';
            } catch (error) {
              console.error(`Failed to fetch user ${userId}:`, error);
              names[userId] = 'Unknown User';
            }
          })
        );

        setUserNames(names);
      } catch (err: any) {
        console.error('Failed to load AI usage report:', err);
        setError(err.message || 'Failed to load usage report');
      } finally {
        setLoading(false);
      }
    };

    loadUsageReport();
  }, [organization]);

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getFeatureLabel = (feature: string): string => {
    switch (feature) {
      case 'gemini-image':
        return 'Image Generation';
      case 'google-tts':
        return 'Audio (TTS)';
      case 'veo-video':
        return 'Video Generation';
      default:
        return feature;
    }
  };

  const getFeatureIcon = (feature: string) => {
    switch (feature) {
      case 'gemini-image':
        return <Icons.Image className="w-4 h-4 text-purple-600" />;
      case 'google-tts':
        return <Icons.Audio className="w-4 h-4 text-blue-600" />;
      case 'veo-video':
        return <Icons.Youtube className="w-4 h-4 text-red-600" />;
      default:
        return <Icons.Star className="w-4 h-4 text-gray-600" />;
    }
  };

  const getQuotaColor = (used: number, limit: number) => {
    if (limit === 0) return 'gray';
    const percentage = (used / limit) * 100;
    if (percentage >= 100) return 'red';
    if (percentage >= 80) return 'orange';
    if (percentage >= 50) return 'yellow';
    return 'green';
  };

  const colorClasses = {
    green: { bg: 'bg-green-100', text: 'text-green-700', bar: 'bg-green-500' },
    yellow: { bg: 'bg-yellow-100', text: 'text-yellow-700', bar: 'bg-yellow-500' },
    orange: { bg: 'bg-orange-100', text: 'text-orange-700', bar: 'bg-orange-500' },
    red: { bg: 'bg-red-100', text: 'text-red-700', bar: 'bg-red-500' },
    gray: { bg: 'bg-gray-100', text: 'text-gray-500', bar: 'bg-gray-300' }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <AppHeader
        user={user}
        organization={organization}
        onLogout={onLogout}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      {/* Page Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <Icons.ChevronLeft className="w-5 h-5" />
          Back to Dashboard
        </button>

        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Usage Dashboard</h1>
          <p className="text-gray-600">
            Track your organization's monthly AI usage
            {organization && ` - ${organization.name}`}
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Icons.Loader className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-3 text-gray-600">Loading usage data...</span>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-red-800">
              <Icons.X className="w-5 h-5" />
              <span className="font-medium">Error loading usage data</span>
            </div>
            <p className="text-red-700 text-sm mt-1">{error}</p>
          </div>
        )}

        {/* Usage Report */}
        {report && !loading && (
          <div className="space-y-6">
            {/* Tier Badge */}
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                report.tier === 'ministry'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {report.tier === 'ministry' ? 'Ministry Tier' : 'Free Tier'}
              </span>
              <span className="text-sm text-gray-500">
                {report.isLifetime
                  ? 'Lifetime Credits'
                  : (() => {
                      const [year, month] = report.period.split('-');
                      return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                    })()}
              </span>
            </div>

            {/* Monthly Quotas */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Images */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icons.Image className="w-5 h-5 text-purple-600" />
                  <h3 className="font-bold text-gray-900">Images</h3>
                </div>
                <div className={`text-3xl font-bold mb-2 ${colorClasses[getQuotaColor(report.images.used, report.images.limit)].text}`}>
                  {report.images.used}/{report.images.limit}
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
                  <div
                    className={`h-full ${colorClasses[getQuotaColor(report.images.used, report.images.limit)].bar} transition-all`}
                    style={{ width: report.images.limit > 0 ? `${Math.min(100, (report.images.used / report.images.limit) * 100)}%` : '0%' }}
                  />
                </div>
                <p className="text-sm text-gray-500">{report.images.remaining} remaining</p>
              </div>

              {/* TTS */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icons.Audio className="w-5 h-5 text-blue-600" />
                  <h3 className="font-bold text-gray-900">Audio (TTS)</h3>
                </div>
                <div className={`text-3xl font-bold mb-2 ${colorClasses[getQuotaColor(report.tts.used, report.tts.limit)].text}`}>
                  {report.tts.used}/{report.tts.limit}
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
                  <div
                    className={`h-full ${colorClasses[getQuotaColor(report.tts.used, report.tts.limit)].bar} transition-all`}
                    style={{ width: report.tts.limit > 0 ? `${Math.min(100, (report.tts.used / report.tts.limit) * 100)}%` : '0%' }}
                  />
                </div>
                <p className="text-sm text-gray-500">{report.tts.remaining} remaining</p>
              </div>

              {/* Videos */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icons.Youtube className="w-5 h-5 text-red-600" />
                  <h3 className="font-bold text-gray-900">Videos</h3>
                </div>
                {report.videos.limit === 0 ? (
                  <>
                    <div className="text-2xl font-bold text-gray-400 mb-2">Not Available</div>
                    <p className="text-sm text-gray-500">Upgrade to Ministry tier for video generation</p>
                  </>
                ) : (
                  <>
                    <div className={`text-3xl font-bold mb-2 ${colorClasses[getQuotaColor(report.videos.used, report.videos.limit)].text}`}>
                      {report.videos.used}/{report.videos.limit}
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
                      <div
                        className={`h-full ${colorClasses[getQuotaColor(report.videos.used, report.videos.limit)].bar} transition-all`}
                        style={{ width: `${Math.min(100, (report.videos.used / report.videos.limit) * 100)}%` }}
                      />
                    </div>
                    <p className="text-sm text-gray-500">{report.videos.remaining} remaining</p>
                  </>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Activity</h2>

              {report.recentUsage.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Icons.Star className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No AI features used this month yet.</p>
                  <p className="text-sm mt-1">Generate images, audio, or video to see activity here.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {report.recentUsage.map((usage, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center justify-between p-3">
                        <div className="flex items-center gap-3">
                          {usage.feature === 'gemini-image' && usage.imageUrl ? (
                            <img
                              src={usage.imageUrl}
                              alt="Generated"
                              className="w-12 h-12 rounded object-cover border border-gray-200"
                            />
                          ) : usage.feature === 'veo-video' && usage.videoUrl ? (
                            <div className="w-12 h-12 bg-black rounded border border-gray-200 flex items-center justify-center">
                              <Icons.Youtube className="w-6 h-6 text-white" />
                            </div>
                          ) : (
                            getFeatureIcon(usage.feature)
                          )}
                          <div>
                            <div className="font-medium text-gray-900">
                              {getFeatureLabel(usage.feature)}
                            </div>
                            {usage.details && (
                              <div className="text-sm text-gray-600 truncate max-w-xs">{usage.details}</div>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">{formatDate(usage.timestamp)}</div>
                          <div className="text-xs text-gray-500">{userNames[usage.userId] || 'Loading...'}</div>
                        </div>
                      </div>
                      {usage.feature === 'google-tts' && usage.audioUrl && (
                        <div className="px-3 pb-3">
                          <audio src={usage.audioUrl} controls className="w-full h-8" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Info Card */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <Icons.Quiz className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-2">
                    {report.isLifetime ? 'About Your Starter Credits' : 'About Monthly Quotas'}
                  </h3>
                  <ul className="text-sm text-blue-800 space-y-1">
                    {report.tier === 'free' ? (
                      <>
                        <li>Your free account includes {report.images.limit} images, {report.tts.limit} audio, and {report.videos.limit} videos</li>
                        <li>These are lifetime starter credits - once used, upgrade to get more</li>
                        <li>Upgrade to Ministry tier ($24.99/month) for monthly rolling credits</li>
                      </>
                    ) : (
                      <>
                        <li>Ministry tier includes {report.images.limit} images, {report.tts.limit} audio, and {report.videos.limit} videos per month</li>
                        <li>Quotas reset on the 1st of each month</li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
